/** @jsxImportSource @emotion/react */
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
} from '@mui/material';
import { Selectable } from '@zip/business-components';
import { renderFieldError } from 'utils/validators';
import * as styles from '../ActivationFlow.styles';

type ActivationRadioGroupBaseProps = {
  name: string;
  label: string;
  error?: boolean;
  helperText?: string;
};

type ActivationBooleanRadioGroupProps = ActivationRadioGroupBaseProps & {
  options?: undefined;
  value?: boolean;
  onChange: (value: boolean) => void;
};

type ActivationOptionRadioGroupProps = ActivationRadioGroupBaseProps & {
  options: Selectable[];
  value?: string;
  onChange: (value: string) => void;
};

export type ActivationRadioGroupProps =
  | ActivationBooleanRadioGroupProps
  | ActivationOptionRadioGroupProps;

function BooleanVariant({
  name,
  label,
  error = false,
  helperText,
  value,
  onChange,
}: ActivationBooleanRadioGroupProps): JSX.Element {
  const labelId = `${name}-label`;
  const stringValue = value === undefined ? '' : String(value);

  return (
    <FormControl error={error} css={styles.stepOneRadioField}>
      <p className="body1" id={labelId} css={styles.title}>
        {label}
      </p>
      <RadioGroup
        name={name}
        value={stringValue}
        aria-labelledby={labelId}
        css={styles.stepOneRadioGroup}
        onChange={(_, nextValue) => onChange(nextValue === 'true')}
      >
        <FormControlLabel
          value="true"
          control={<Radio />}
          label={<span className="body2">Yes</span>}
          css={styles.stepOneRadioOption(value === true)}
        />
        <FormControlLabel
          value="false"
          control={<Radio />}
          label={<span className="body2">No</span>}
          css={styles.stepOneRadioOption(value === false)}
        />
      </RadioGroup>
      {helperText && (
        <FormHelperText className="body4">
          {renderFieldError(true, helperText)}
        </FormHelperText>
      )}
    </FormControl>
  );
}

function OptionVariant({
  name,
  label,
  error = false,
  helperText,
  options,
  value = '',
  onChange,
}: ActivationOptionRadioGroupProps): JSX.Element {
  const labelId = `${name}-label`;

  return (
    <FormControl error={error} css={styles.stepOneRadioField}>
      <p className="body1" id={labelId} css={styles.title}>
        {label}
      </p>
      <RadioGroup
        name={name}
        value={value}
        aria-labelledby={labelId}
        css={styles.stepOneRadioGroup}
        onChange={(_, nextValue) => onChange(nextValue)}
      >
        {options.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio />}
            label={<span className="body2">{option.label}</span>}
            css={styles.stepOneRadioOption(value === option.value)}
          />
        ))}
      </RadioGroup>
      {helperText && (
        <FormHelperText className="body4">
          {renderFieldError(true, helperText)}
        </FormHelperText>
      )}
    </FormControl>
  );
}

export function ActivationRadioGroup(
  props: ActivationRadioGroupProps
): JSX.Element {
  if (props.options === undefined) {
    return <BooleanVariant {...(props as ActivationBooleanRadioGroupProps)} />;
  }
  return <OptionVariant {...(props as ActivationOptionRadioGroupProps)} />;
}
